import { create, type StateCreator } from 'zustand'
import { devtools } from 'zustand/middleware'

import { GenericErrorModel } from '@/app/models/genericErrorModel'
import { zustandPlugin } from '@/modules/shared/openreplay'

type State = {
  /** Błąd pokazywany na osobnej stronie /error. Null = nic się nie stało. */
  error: GenericErrorModel | null
}

type Actions = {
  showError: (error: GenericErrorModel) => void
  clearError: () => void
}

/**
 * Ten sam cast co w `taskTrackerStore`: OpenReplay oddaje `zustandPlugin` przez
 * `as unknown as`, co gubi ograniczenie mutatorów i typuje `set` jako wariant
 * dwuargumentowy. Nazwanie mutatora przywraca trzeci argument, czyli czytelne nazwy
 * akcji w Redux DevTools i w nagraniu sesji.
 */
type StoreMutators = [['zustand/devtools', never]]

const trackedCreator = zustandPlugin as unknown as <T>(
  creator: StateCreator<T, StoreMutators, []>,
  name?: string,
) => StateCreator<T, StoreMutators, []>

/**
 * Błąd całostronicowy (404 / NOT_MIGRATED_COMPANY), wcześniej slice `genericError`.
 *
 * Slice miał dokładnie jednego pisarza (`baseService`) i jednego czytelnika (strona
 * błędu), więc przenosiny są zamknięte w tych dwóch miejscach. Chodziło o to, żeby
 * `baseService` przestał potrzebować reduxowego `dispatch` — to on przewlekał go przez
 * każde wywołanie API i przez sygnatury 26 plików serwisowych.
 */
export const useGenericErrorStore = create<State & Actions>()(
  devtools(
    trackedCreator(
      (set) => ({
        error: null,
        showError: (error) => set({ error }, false, 'show'),
        clearError: () => set({ error: null }, false, 'clear'),
      }),
      'generic-error',
    ),
    { name: 'EasyTacho', store: 'generic-error' },
  ),
)

/** Poza Reactem (baseService) — bez hooka. */
export const showGenericError = (error: GenericErrorModel) =>
  useGenericErrorStore.getState().showError(error)
